import React from "react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  ArrowSquareOut,
  User,
} from "@phosphor-icons/react";
import {
  SharedCalendarEvent,
  OWNER_STYLES,
  inferEventType,
} from "./types";

interface EventDetailDialogProps {
  event: SharedCalendarEvent | null;
  onClose: () => void;
}

const formatRange = (event: SharedCalendarEvent): string => {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const sameDay =
    start.toDateString() === end.toDateString();
  if (event.isAllDay) {
    return format(start, "EEEE d MMMM yyyy", { locale: nl }) + " · Hele dag";
  }
  if (sameDay) {
    return `${format(start, "EEEE d MMMM yyyy", { locale: nl })} · ${format(
      start,
      "HH:mm",
    )} – ${format(end, "HH:mm")}`;
  }
  return `${format(start, "d MMM HH:mm", { locale: nl })} – ${format(
    end,
    "d MMM HH:mm",
    { locale: nl },
  )}`;
};

export const EventDetailDialog: React.FC<EventDetailDialogProps> = ({
  event,
  onClose,
}) => {
  if (!event) return null;
  const ownerStyle = OWNER_STYLES[event.owner];
  const type = inferEventType(event);

  return (
    <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-full"
              style={{ backgroundColor: ownerStyle.accentHex }}
              aria-hidden="true"
            />
            <span className="text-neutral-secondary-mid text-xs font-medium uppercase">
              {ownerStyle.label} · {ownerStyle.description} · {type}
            </span>
          </div>
          <DialogTitle className="text-neutral-primary text-xl">
            {event.subject}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          <div className="text-neutral-primary flex items-center gap-2">
            <Clock size={16} className="text-neutral-secondary-mid" />
            {formatRange(event)}
          </div>

          {event.location && (
            <div className="text-neutral-primary flex items-center gap-2">
              <MapPin size={16} className="text-neutral-secondary-mid" />
              {event.location}
            </div>
          )}

          {event.organizer && (
            <div className="text-neutral-primary flex items-center gap-2">
              <User size={16} className="text-neutral-secondary-mid" />
              <span>
                Organisator:{" "}
                <span className="font-medium">
                  {event.organizer.name || event.organizer.email}
                </span>
              </span>
            </div>
          )}

          {event.attendees.length > 0 && (
            <div>
              <div className="text-neutral-primary mb-1 flex items-center gap-2 font-medium">
                <Users size={16} className="text-neutral-secondary-mid" />
                Deelnemers ({event.attendees.length})
              </div>
              <ul className="space-y-1 pl-6">
                {event.attendees.map((a, idx) => (
                  <li
                    key={`${a.email ?? a.name ?? idx}-${idx}`}
                    className="text-neutral-primary flex flex-wrap items-baseline gap-x-2 text-xs"
                  >
                    <span className="font-medium">
                      {a.name || a.email || "Onbekend"}
                    </span>
                    {a.email && a.name && (
                      <span className="text-neutral-secondary-mid">
                        {a.email}
                      </span>
                    )}
                    {a.status && a.status !== "none" && (
                      <span className="text-neutral-secondary-mid border-neutral-300 rounded border px-1.5 py-0 text-[10px] uppercase">
                        {a.status}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(event.bodyHtml || event.bodyPreview) && (
            <div>
              <div className="text-neutral-primary mb-1 flex items-center gap-2 font-medium">
                <Calendar size={16} className="text-neutral-secondary-mid" />
                Details
              </div>
              {event.bodyHtml ? (
                <div
                  className="prose prose-sm border-border bg-muted text-neutral-primary max-w-none rounded-md border p-3 text-xs"
                  dangerouslySetInnerHTML={{ __html: event.bodyHtml }}
                />
              ) : (
                <p className="border-border bg-muted text-neutral-primary rounded-md border p-3 text-xs whitespace-pre-wrap">
                  {event.bodyPreview}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          {event.webLink && (
            <Button
              variant="outline"
              onClick={() => window.open(event.webLink!, "_blank")}
            >
              <ArrowSquareOut size={16} className="mr-2" />
              Open in Outlook
            </Button>
          )}
          <Button onClick={onClose}>Sluiten</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
